import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import { FiUsers, FiTrendingUp, FiCheckCircle, FiClock, FiDollarSign, FiTarget, FiActivity, FiPlus, FiArrowUp, FiArrowDown } from 'react-icons/fi';

const CrmDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [recentLeads, setRecentLeads] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [leadsRes, analyticsRes, tasksRes, dealsRes] = await Promise.all([
        axios.get('/api/crm/leads?limit=5&sortBy=createdAt&sortOrder=DESC').catch(err => {
          return { data: { leads: [], stats: [] } };
        }),
        axios.get('/api/crm/analytics').catch(err => {
          return { data: null };
        }),
        axios.get('/api/crm/tasks?status=Pending&limit=5').catch(err => {
          return { data: { tasks: [] } };
        }),
        axios.get('/api/crm/deals?limit=5').catch(err => {
          return { data: { deals: [] } };
        })
      ]);

      setRecentLeads(leadsRes.data.leads || []);
      setStats(leadsRes.data.stats || []);
      setAnalytics(analyticsRes.data);
      setTasks(tasksRes.data.tasks || []);
      setDeals(dealsRes.data.deals || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const getStatusCount = (status) => {
    return stats?.find(s => s.status === status)?.count || 0;
  };

  const getTotalLeads = () => {
    return stats?.reduce((sum, s) => sum + parseInt(s.count || 0), 0) || 0;
  };



  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  );

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">CRM Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's your sales overview</p>
        </div>
        <button
          onClick={() => navigate('/crm/leads/new')}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus size={20} />
          New Lead
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiUsers className="text-blue-600" size={24} />
            </div>
            <span className="flex items-center text-green-600 text-sm font-medium">
              <FiArrowUp size={16} />
              12%
            </span>
          </div>
          <div className="text-sm text-gray-600 mb-1">Total Leads</div>
          <div className="text-3xl font-bold text-gray-900">{getTotalLeads()}</div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <FiCheckCircle className="text-green-600" size={24} />
            </div>
            <span className="flex items-center text-green-600 text-sm font-medium">
              <FiArrowUp size={16} />
              8%
            </span>
          </div>
          <div className="text-sm text-gray-600 mb-1">Converted</div>
          <div className="text-3xl font-bold text-gray-900">{getStatusCount('Converted')}</div>
        </div>



        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-orange-100 rounded-lg">
              <FiTarget className="text-orange-600" size={24} />
            </div>
            <span className="flex items-center text-red-600 text-sm font-medium">
              <FiArrowDown size={16} />
              3%
            </span>
          </div>
          <div className="text-sm text-gray-600 mb-1">Pending Tasks</div>
          <div className="text-3xl font-bold text-gray-900">{tasks.length}</div>
        </div>
      </div>

      {/* Lead Status Overview */}
      <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <FiActivity className="text-blue-600" />
          Lead Status Overview
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {['New', 'Contacted', 'Qualified', 'Proposal', 'Converted', 'Lost'].map((status) => (
            <div key={status} className="text-center p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
              onClick={() => navigate(`/crm/leads?status=${status}`)}>
              <div className="text-2xl font-bold text-gray-900">{getStatusCount(status)}</div>
              <div className="text-sm text-gray-600 mt-1">{status}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Leads */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FiUsers className="text-blue-600" />
              Recent Leads
            </h2>
            <button
              onClick={() => navigate('/crm/leads')}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View All →
            </button>
          </div>
          <div className="space-y-3">
            {recentLeads.map((lead) => (
              <div
                key={lead.id}
                onClick={() => navigate(`/crm/leads/${lead.id}`)}
                className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:shadow-sm cursor-pointer transition-all"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{lead.name}</div>
                    <div className="text-sm text-gray-600 mt-1">
                      {lead.company || 'No company'} • {lead.email || 'No email'}
                    </div>
                    {lead.value > 0 && (
                      <div className="text-sm text-green-600 font-medium mt-1">
                        ${parseFloat(lead.value).toLocaleString()}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      lead.status === 'New' ? 'bg-blue-100 text-blue-800' :
                      lead.status === 'Contacted' ? 'bg-yellow-100 text-yellow-800' :
                      lead.status === 'Qualified' ? 'bg-green-100 text-green-800' :
                      lead.status === 'Converted' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {lead.status}
                    </span>
                    {lead.priority && (
                      <span className={`text-xs font-medium ${
                        lead.priority === 'High' ? 'text-red-600' :
                        lead.priority === 'Medium' ? 'text-yellow-600' :
                        'text-green-600'
                      }`}>
                        {lead.priority} Priority
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {recentLeads.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <FiUsers size={48} className="mx-auto mb-3 text-gray-300" />
                <p>No leads yet. Create your first lead!</p>
              </div>
            )}
          </div>
        </div>

        {/* Pending Tasks */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FiClock className="text-orange-600" />
              Pending Tasks
            </h2>
            <button
              onClick={() => navigate('/crm/tasks')}
              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
            >
              View All →
            </button>
          </div>
          <div className="space-y-3">
            {tasks.map((task) => (
              <div key={task.id} className="p-3 border border-gray-200 rounded-lg hover:border-orange-300 transition-colors">
                <div className="flex items-start gap-2 mb-2">
                  <FiClock className="text-orange-600 mt-1" size={16} />
                  <div className="flex-1">
                    <div className="font-medium text-sm text-gray-900">{task.taskTitle}</div>
                    {task.lead && (
                      <div className="text-xs text-gray-600 mt-1">{task.lead.name}</div>
                    )}
                  </div>
                </div>
                {task.dueDate && (
                  <div className="text-xs text-gray-500 mt-2">
                    Due: {new Date(task.dueDate).toLocaleDateString()}
                  </div>
                )}
              </div>
            ))}
            {tasks.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FiCheckCircle size={32} className="mx-auto mb-2 text-gray-300" />
                <p className="text-sm">All caught up!</p>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl shadow-sm p-6 text-white">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/crm/leads/new')}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm p-4 rounded-lg transition-colors text-center"
          >
            <FiPlus className="mx-auto mb-2" size={24} />
            <div className="text-sm font-medium">New Lead</div>
          </button>

          <button
            onClick={() => navigate('/crm/tasks')}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm p-4 rounded-lg transition-colors text-center"
          >
            <FiCheckCircle className="mx-auto mb-2" size={24} />
            <div className="text-sm font-medium">My Tasks</div>
          </button>
          <button
            onClick={() => navigate('/crm/analytics')}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm p-4 rounded-lg transition-colors text-center"
          >
            <FiActivity className="mx-auto mb-2" size={24} />
            <div className="text-sm font-medium">Analytics</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CrmDashboard;
